import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';

/**
 * Admin review of rx-store storefronts (backend docs/Storefronts.md,
 * contract v5 A/E/F). Full admins only: the backend checks `FullAdminUser`
 * on every route; the screen's `RequireFullAdmin` is UX only. Store owners
 * are Supabase users, not patients; every number here is an aggregate (the
 * backend never returns patient names, emails or medical details on these
 * routes).
 */

export type StorefrontStatus = 'draft' | 'live' | 'paused';
export type StorefrontReviewStatus =
  | 'draft'
  | 'submitted'
  | 'changes_requested'
  /** An approved store with a published change waiting for review (the
   *  live store keeps serving its last approved version meanwhile). The
   *  stored review status stays `approved` (`base_review_status`). */
  | 'changes_pending_review'
  | 'approved'
  | 'rejected';
export type StorefrontTemplate = 'performance' | 'renew' | 'vitality' | 'clinical';
export type StorefrontImageStyle =
  | 'clinical-white'
  | 'amber-apothecary'
  | 'matte-black'
  | 'tinted-glass';
export type StorefrontPlanKey = 'monthly' | 'quarterly' | 'six_month' | 'twelve_month';

/** Which owner publishes of a live or paused store wait for review
 *  (contract v7): `every_change` (everything, products, prices and promo
 *  codes included), `copy_and_images` (the default: new wording and new
 *  images), `flagged_only` (wording the claims screen flags, and new
 *  images) or `never`. Chosen on the store's first approval. */
export type StorefrontReviewPolicy = 'every_change' | 'copy_and_images' | 'flagged_only' | 'never';

/** A store's review policy; an older backend sends only the
 *  `review_every_change` switch it replaced. */
export function reviewPolicyOf(store: {
  review_policy?: StorefrontReviewPolicy | string | null;
  review_every_change?: boolean;
}): StorefrontReviewPolicy {
  const raw = store.review_policy;
  if (
    raw === 'every_change' ||
    raw === 'copy_and_images' ||
    raw === 'flagged_only' ||
    raw === 'never'
  ) {
    return raw;
  }
  return store.review_every_change ? 'every_change' : 'copy_and_images';
}

/** List tabs. Live/Paused filter on `status`, the rest on `review_status`. */
export type StorefrontListTab =
  | 'submitted'
  | 'changes_pending_review'
  | 'changes_requested'
  | 'live'
  | 'paused'
  | 'draft'
  | 'rejected';

export type StorefrontListCounts = Record<StorefrontListTab, number>;

/** A list filter: one counted tab, or every store (no filter; this also
 *  reaches stores no count bucket covers, such as an approved draft). */
export type StorefrontListFilter = StorefrontListTab | 'all';

export interface AdminStorefrontListItem {
  id: string;
  slug: string;
  name: string;
  status: StorefrontStatus;
  review_status: StorefrontReviewStatus;
  owner_email: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  updated_at: string | null;
  products_count: number;
  logo_url: string | null;
  accent_color: string | null;
  /** A field change recorded since the last review (published or direct
   *  edits; never an unpublished draft). Older backends: `updated_at >
   *  reviewed_at`. */
  edited_since_review?: boolean;
  /** Recorded field changes since the last review. */
  edits_since_review?: number;
  /** One of those changes was a photo or the logo. */
  media_changed_since_review?: boolean;
  /** Owner publishes that went live without a review since the last one. */
  published_since_review?: number;
  /** When the newest of those went live. */
  last_published_at?: string | null;
  /** Every owner publish of this store waits for review (pre-v7; read
   *  through `reviewPolicyOf`). */
  review_every_change?: boolean;
  /** Which owner publishes wait for review (contract v7). */
  review_policy?: StorefrontReviewPolicy;
  /** Distinct patients with an order from this store or created by it. */
  customers_count?: number | null;
  /** The stored review status when `review_status` is the effective
   *  `changes_pending_review`. */
  base_review_status?: StorefrontReviewStatus;
  /** The backend's count bucket for the row (newer backends). */
  bucket?: StorefrontListTab | null;
}

export interface AdminStorefrontList {
  counts: StorefrontListCounts;
  /** Stores with an owner publish that went live without a review since
   *  their last one (`counts.published_unreviewed`); null from an older
   *  backend that does not count them. */
  publishedUnreviewed: number | null;
  items: AdminStorefrontListItem[];
}

export interface StorefrontDesignSection {
  key: string;
  enabled: boolean;
  content?: Record<string, unknown>;
}

export interface StorefrontDesign {
  template?: StorefrontTemplate;
  theme?: { primary?: string | null; secondary?: string | null };
  product_image_style?: StorefrontImageStyle;
  sections?: StorefrontDesignSection[];
  /** The line under the store name (contract v7): the template's "Curated
   *  by {name}" (`default`), the owner's own words (`custom`, may use
   *  `{name}`) or none (`hidden`). */
  byline?: {
    mode?: 'default' | 'custom' | 'hidden' | string;
    /** The owner's text as saved (`{name}` unfilled). */
    text?: string | null;
    /** As the page shows it (`{name}` filled; the admin detail sends it). */
    text_resolved?: string | null;
  } | null;
}

export interface AdminStorefrontPlan {
  plan: StorefrontPlanKey | string;
  interval_months: number | null;
  price_cents: number;
  /** Null while the catalog (Sanity) cannot be read. */
  floor_cents: number | null;
  earning_cents: number | null;
  active: boolean;
}

export interface AdminStorefrontProduct {
  canonical_product_id: string;
  name: string | null;
  form: string | null;
  plans: AdminStorefrontPlan[];
}

export interface AdminStorefrontMember {
  email: string | null;
  /** Collected when the invite is redeemed or at sign-up. */
  full_name?: string | null;
  access: 'owner' | 'editor' | string;
  joined_at: string | null;
}

export interface AdminStorefrontPromoCode {
  id: string;
  code: string;
  percent_off: number | null;
  amount_off_cents: number | null;
  duration: 'once' | 'forever' | string;
  max_redemptions: number | null;
  redemptions: number | null;
  expires_at: string | null;
  active: boolean;
  description?: string | null;
}

export type StorefrontEventAction =
  | 'submitted'
  | 'resubmitted'
  | 'approved'
  | 'changes_requested'
  | 'rejected'
  | 'paused'
  | 'unpaused'
  | 'invite_redeemed';

export interface AdminStorefrontEvent {
  id?: string;
  action: StorefrontEventAction | string;
  note: string | null;
  actor: string | null;
  created_at: string;
  /** `review` (a review or status action) or `change` (one changed field,
   *  with its old and new value). */
  kind?: 'review' | 'change' | string;
  field?: string | null;
  old_value?: string | null;
  new_value?: string | null;
}

/** Same shape as the owner metrics `totals` (contract v5 E). */
export interface StorefrontMetricsTotals {
  orders: number;
  paid_charges: number;
  gross_cents: number;
  earnings_cents: number;
  refunds_cents: number;
  active_subscriptions: number;
  customers: number;
  visits: number;
}

export type StorefrontPayoutStatus = 'pending' | 'paid' | 'void';

export interface StorefrontPayout {
  id: string;
  period_start: string;
  period_end: string;
  amount_cents: number;
  status: StorefrontPayoutStatus;
  paid_at: string | null;
  reference: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string | null;
  paid_by?: string | null;
  voided_by?: string | null;
  voided_at?: string | null;
  /** Why it was voided (owners read it). */
  void_reason?: string | null;
}

export interface StorefrontPayouts {
  /** Earnings minus paid payouts. */
  balance_cents: number;
  /** Next Monday; informational. */
  next_payout_date: string | null;
  history: StorefrontPayout[];
  method?: string | null;
  note?: string | null;
}

export interface AdminStorefrontDetail {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  accent_color: string | null;
  headline: string | null;
  subheadline: string | null;
  support_email: string | null;
  medical_group_name: string | null;
  status: StorefrontStatus;
  review_status: StorefrontReviewStatus;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  submitted_at: string | null;
  rules_accepted_at: string | null;
  owner_email: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The review baseline (newer backends): field changes recorded since
   *  `reviewed_at`, which moves on every store approval, change approval,
   *  revert and "mark reviewed". Never an unpublished draft. */
  edited_since_review?: boolean;
  edits_since_review?: number;
  media_changed_since_review?: boolean;
  /** Owner publishes that went live without a review since the last one. */
  published_since_review?: number;
  last_published_at?: string | null;
  /** Pre-v7 switch; read the policy through `reviewPolicyOf`. */
  review_every_change?: boolean;
  /** Which owner publishes wait for review (`POST …/review-policy`). */
  review_policy?: StorefrontReviewPolicy;
  /** Distinct patients with an order from this store or created by it. */
  customers_count?: number | null;
  /** What "Revert to last approved" would do (a live or paused store with
   *  an approved snapshot, else null). */
  revert_preview?: StorefrontRevertPreview | null;
  design: StorefrontDesign | null;
  members: AdminStorefrontMember[];
  products: AdminStorefrontProduct[];
  promo_codes: AdminStorefrontPromoCode[];
  events: AdminStorefrontEvent[];
  metrics: StorefrontMetricsTotals | null;
  payouts: Partial<StorefrontPayouts> | null;
  /** The stored review status when `review_status` is the effective
   *  `changes_pending_review`. */
  base_review_status?: StorefrontReviewStatus;
  /** The name the store's copy signs with (public): the owner-set value,
   *  else the first owner's first name. */
  creator_name?: string | null;
  /** The owner-set creator name (null = derived from the owner's name). */
  creator_name_custom?: string | null;
  owner_full_name?: string | null;
  owner_profile?: StorefrontOwnerProfile | null;
  /** The backend's claims scan of the published copy (absent on older
   *  backends: the screen then runs its own mirror of the rules). */
  claim_findings?: StorefrontClaimFinding[];
  pending_changes?: StorefrontPendingChanges | null;
  /** The owner's unpublished edits (not live, not submitted). */
  draft?: {
    draft_updated_at: string | null;
    draft_updated_by: string | null;
    changes: StorefrontContentChange[];
  } | null;
  versions?: StorefrontVersion[];
  /** The medical groups a store may name (`STOREFRONT_MEDICAL_GROUPS`). */
  medical_group_options?: string[];
}

/** One automatic flag from the backend's claims scan (contract D2): the
 *  field path (`headline`, `design.hero.headline`, `design.faq.items.2.a`),
 *  the matched phrase and its offsets in that field's text. Flags are an aid,
 *  never a verdict: no flags does not mean the copy is compliant. */
export interface StorefrontClaimFinding {
  field: string;
  phrase: string;
  rule: string;
  category?: string | null;
  /** `block`: an owner save refuses it; `review`: a human decides. */
  severity: 'block' | 'review' | string;
  start: number;
  end: number;
  text?: string;
}

/** One field-level change (`{field, old, new}`), as in a change event. */
export interface StorefrontContentChange {
  field: string;
  old: unknown;
  new: unknown;
}

/** A published change waiting for review (contract D1). */
export interface StorefrontPendingChanges {
  id: string;
  submitted_at: string | null;
  submitted_by: string | null;
  changes: StorefrontContentChange[];
  /** Claim flags in the new or changed copy. */
  flags: StorefrontClaimFinding[];
  /** Why it needs a human: `claims_flags`, `new_image` (with `field`),
   *  `store_in_review`. */
  reasons: { reason: string; field?: string | null; count?: number | null }[];
  /** The store as it would read once approved. */
  content?: Record<string, unknown> | null;
}

/** A published snapshot of the store (every approval, clean publish and
 *  revert). `approved` and `baseline` are what a revert goes back to. */
export interface StorefrontVersion {
  id: string;
  version: number;
  kind: 'baseline' | 'approved' | 'published' | 'reverted' | string;
  note: string | null;
  actor: string | null;
  created_at: string;
}

/** What a revert puts back and what it leaves live (round 6). `restores`
 *  is the published content against the snapshot (`old` the live value,
 *  `new` the one restored); `not_restored` are the product, price and promo
 *  edits since that version, which a revert leaves live and outstanding. */
export interface StorefrontRevertPreview {
  version: number;
  kind: StorefrontVersion['kind'];
  created_at: string | null;
  restores: StorefrontContentChange[];
  not_restored: {
    field: string;
    old_value: string | null;
    new_value: string | null;
    actor: string | null;
    created_at: string | null;
  }[];
}

/** Who runs the store, for vetting (owner-entered; never public). */
export interface StorefrontOwnerProfile {
  social_handle?: string | null;
  social_platform?: string | null;
  audience_size?: string | null;
  audience_description?: string | null;
}

export type StorefrontReviewAction = 'approve' | 'request_changes' | 'reject';

export type StorefrontInviteState = 'active' | 'redeemed' | 'expired' | 'revoked';

export interface StorefrontInvite {
  id: string;
  code: string;
  note: string | null;
  expires_at: string | null;
  redeemed_at: string | null;
  storefront_id: string | null;
  created_at: string | null;
  storefront: { slug: string; name: string; status: StorefrontStatus } | null;
  /** Sent by newer backends; older ones are read from the dates. */
  state?: StorefrontInviteState | string | null;
  revoked_at?: string | null;
  created_by_email?: string | null;
  redeemed_email?: string | null;
}

/**
 * The state the admin's screen showed when a review or status action
 * started. The backend refuses the action (`409 storefront_review_stale`)
 * when the store has moved on since: another admin's decision, or an owner
 * edit (`updated_at` moves on content edits only).
 */
export interface StorefrontExpectedState {
  expected_review_status: StorefrontReviewStatus;
  expected_status: StorefrontStatus;
  expected_updated_at: string | null;
}

export function expectedState(
  store: Pick<AdminStorefrontDetail, 'review_status' | 'status' | 'updated_at'>,
): StorefrontExpectedState {
  return {
    expected_review_status: store.review_status,
    expected_status: store.status,
    expected_updated_at: store.updated_at ?? null,
  };
}

const TABS: StorefrontListTab[] = [
  'submitted',
  'changes_pending_review',
  'changes_requested',
  'live',
  'paused',
  'draft',
  'rejected',
];

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function countFor(raw: unknown, tab: StorefrontListTab): number {
  const value = (raw as Record<string, unknown> | null)?.[tab];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** `{counts, items}`; an older backend answered `{storefronts}` without
 *  counts, so both are read. */
function normalizeList(raw: unknown): AdminStorefrontList {
  const body = (raw ?? {}) as Record<string, unknown>;
  const items = asArray<AdminStorefrontListItem>(body.items ?? body.storefronts).map(
    (row) => ({
      ...row,
      review_status: row.review_status ?? 'draft',
      products_count:
        typeof row.products_count === 'number'
          ? row.products_count
          : asArray((row as unknown as { products?: unknown }).products).length,
    }),
  );
  const counts = Object.fromEntries(
    TABS.map((tab) => [tab, countFor(body.counts, tab)]),
  ) as StorefrontListCounts;
  const unreviewed = (body.counts as Record<string, unknown> | null)?.published_unreviewed;
  return {
    counts,
    publishedUnreviewed:
      typeof unreviewed === 'number' && Number.isFinite(unreviewed) ? unreviewed : null,
    items,
  };
}

function normalizeDetail(raw: unknown): AdminStorefrontDetail {
  const body = (raw ?? {}) as Record<string, unknown>;
  const metrics = (body.metrics ?? body.metrics_summary ?? null) as
    | (StorefrontMetricsTotals & { totals?: StorefrontMetricsTotals })
    | null;
  return {
    ...(body as unknown as AdminStorefrontDetail),
    review_status: (body.review_status as StorefrontReviewStatus) ?? 'draft',
    design: (body.design as StorefrontDesign | null) ?? null,
    members: asArray<AdminStorefrontMember>(body.members),
    products: asArray<AdminStorefrontProduct>(body.products).map((p) => ({
      ...p,
      plans: asArray<AdminStorefrontPlan>(p.plans),
    })),
    promo_codes: asArray<AdminStorefrontPromoCode>(body.promo_codes),
    events: asArray<AdminStorefrontEvent>(body.events),
    versions: asArray<StorefrontVersion>(body.versions),
    medical_group_options: asArray<unknown>(body.medical_group_options).filter(
      (name): name is string => typeof name === 'string' && name.trim() !== '',
    ),
    metrics: metrics?.totals ?? metrics,
    payouts: (body.payouts ?? body.payouts_summary ?? null) as Partial<StorefrontPayouts> | null,
    revert_preview: normalizeRevertPreview(body.revert_preview),
  };
}

function normalizeRevertPreview(raw: unknown): StorefrontRevertPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const preview = raw as Partial<StorefrontRevertPreview>;
  return {
    version: typeof preview.version === 'number' ? preview.version : 0,
    kind: preview.kind ?? 'approved',
    created_at: preview.created_at ?? null,
    restores: asArray<StorefrontContentChange>(preview.restores),
    not_restored: asArray<StorefrontRevertPreview['not_restored'][number]>(preview.not_restored),
  };
}

export const storefrontAdminKeys = {
  all: ['admin', 'storefronts'] as const,
  list: (tab: StorefrontListFilter, q: string) =>
    ['admin', 'storefronts', 'list', tab, q] as const,
  detail: (id: string) => ['admin', 'storefronts', 'detail', id] as const,
  payouts: (id: string) => ['admin', 'storefronts', 'payouts', id] as const,
  invites: ['admin', 'storefront-invites'] as const,
};

/**
 * The backend's count bucket for a store (`_count_bucket` in
 * app/services/storefront_review.py): submitted, changes requested and
 * rejected by review status first, then live / paused by status, then
 * never-submitted drafts; an approved draft is in no bucket. The tabs
 * filter on this same function, so a tab lists exactly the stores its
 * count counts (filtering on `status=live` alone also listed a live store
 * with changes requested, which is counted under Changes requested).
 */
export function storefrontBucket(
  row: Pick<AdminStorefrontListItem, 'status' | 'review_status'> &
    Partial<Pick<AdminStorefrontListItem, 'bucket'>>,
): StorefrontListTab | null {
  if (row.bucket && (TABS as string[]).includes(row.bucket)) return row.bucket;
  const review = row.review_status ?? 'draft';
  const status = row.status ?? 'draft';
  if (
    review === 'submitted' ||
    review === 'changes_pending_review' ||
    review === 'changes_requested' ||
    review === 'rejected'
  ) {
    return review;
  }
  if (status === 'live' || status === 'paused') return status;
  if (review === 'draft') return 'draft';
  return null;
}

/** Every store matching `q` (one request for all tabs), with the backend's
 *  global counts. The screen buckets the rows with `storefrontBucket`, so
 *  search spans every tab and each tab can show how many results it has. */
export function useAdminStorefronts(q: string) {
  return useQuery({
    queryKey: storefrontAdminKeys.list('all', q),
    queryFn: () => fetchStorefrontList(q),
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}

async function fetchStorefrontList(q: string): Promise<AdminStorefrontList> {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  const query = params.toString();
  return normalizeList(await apiFetch<unknown>(`/admin/storefronts${query ? `?${query}` : ''}`));
}

export function useAdminStorefront(id: string | undefined) {
  return useQuery({
    queryKey: storefrontAdminKeys.detail(id ?? ''),
    queryFn: async () =>
      normalizeDetail(
        await apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id ?? '')}`),
      ),
    enabled: Boolean(id),
    staleTime: 10_000,
    // An unknown store is a 404: say so at once instead of retrying it.
    retry: (failures, error) =>
      (error as { status?: number } | null)?.status !== 404 && failures < 2,
  });
}

export function useAdminStorefrontPayouts(id: string | undefined) {
  return useQuery({
    queryKey: storefrontAdminKeys.payouts(id ?? ''),
    queryFn: async () => {
      const body = await apiFetch<Partial<StorefrontPayouts>>(
        `/admin/storefronts/${encodeURIComponent(id ?? '')}/payouts`,
      );
      return {
        balance_cents: body.balance_cents ?? 0,
        next_payout_date: body.next_payout_date ?? null,
        history: asArray<StorefrontPayout>(body.history),
        method: body.method ?? null,
        note: body.note ?? null,
      } satisfies StorefrontPayouts;
    },
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

/** Every write on one store refreshes its detail, payouts and the lists
 *  (tab counts move with review and status changes). */
function useInvalidateStore(id: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: storefrontAdminKeys.detail(id) }),
      queryClient.invalidateQueries({ queryKey: storefrontAdminKeys.payouts(id) }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'storefronts', 'list'] }),
    ]);
}

export function useReviewStorefront(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: (
      body: {
        action: StorefrontReviewAction;
        note: string | null;
        medical_group_name: string | null;
        /** Required on a store's first approval (contract v7). */
        review_policy?: StorefrontReviewPolicy;
      } & StorefrontExpectedState,
    ) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}/review`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
    // A stale tab: load what changed so the screen can show it.
    onError: (error) => isStaleRefusal(error) && invalidate(),
  });
}

export function useSetStorefrontStatus(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: ({
      status,
      reason,
      ...expected
    }: {
      status: 'live' | 'paused';
      /** Shown to owners in their notice (the pause dialog requires it). */
      reason?: string | null;
    } & StorefrontExpectedState) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: JSON.stringify({ status, reason: reason ?? null, ...expected }),
      }),
    onSuccess: invalidate,
    onError: (error) => isStaleRefusal(error) && invalidate(),
  });
}

/** An approved store: acknowledge everything it publishes now (`POST
 *  …/mark-reviewed`). The review baseline moves to now, so "edited since
 *  approval" clears; a change waiting for review stays waiting. `409
 *  storefront_review_stale` when the store changed since the screen loaded. */
export function useMarkStorefrontReviewed(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: (body: { note: string | null; expected_updated_at: string | null }) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}/mark-reviewed`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
    onError: (error) => isStaleRefusal(error) && invalidate(),
  });
}

/** Change which owner publishes of this store wait for review (`POST
 *  …/review-policy`, history `review_policy_changed`). Keeps `updated_at`. */
export function useSetStorefrontReviewPolicy(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: (body: { review_policy: StorefrontReviewPolicy; note: string | null }) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}/review-policy`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

/** Stores for a picker (the CRM's store filter): id, slug and name, by
 *  name. It reads the Storefronts screen's unfiltered list (one cache), and
 *  is full admins only (the list is `FullAdminUser`), so it is off for
 *  other admins and a refusal is not retried. */
export function useStorefrontChoices(enabled: boolean) {
  return useQuery({
    queryKey: storefrontAdminKeys.list('all', ''),
    queryFn: () => fetchStorefrontList(''),
    select: (list: AdminStorefrontList) =>
      list.items
        .map((row) => ({ id: row.id, slug: row.slug, name: row.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** `409 storefront_review_stale`: the store moved on since the screen
 *  loaded, and the backend changed nothing. */
export function isStaleRefusal(error: unknown): boolean {
  const code = (error as { code?: string } | null)?.code;
  return code === 'storefront_review_stale' || code === 'storefront_revision_stale';
}

/** Re-read one store now (a review dialog opening checks it is current). */
export function useRefreshStorefront(id: string) {
  const queryClient = useQueryClient();
  return () => queryClient.refetchQueries({ queryKey: storefrontAdminKeys.detail(id) });
}

export interface StorefrontSettingsInput {
  name?: string;
  slug?: string;
  support_email?: string;
  medical_group_name?: string | null;
}

/** Admin edit of a store's name, slug, support email and medical group
 *  (`PATCH /admin/storefronts/{id}`; only changed fields are sent). */
export function useUpdateStorefrontSettings(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: (body: StorefrontSettingsInput) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

/** A one-hour preview link: the published store (`live`), the owner's
 *  unpublished draft, or the change waiting for review (`pending`). */
export function useStorefrontPreviewToken(id: string) {
  return useMutation({
    mutationFn: (content: 'live' | 'draft' | 'pending' = 'live') =>
      apiFetch<{ url: string; expires_at: string | null }>(
        `/admin/storefronts/${encodeURIComponent(id)}/preview-token${
          content === 'live' ? '' : `?content=${content}`
        }`,
        { method: 'POST' },
      ),
  });
}

/** Approve (publish) or reject the change waiting for review (`POST
 *  …/changes`). `revision_id` is the change the screen showed: `409
 *  storefront_revision_stale` when another change replaced it. */
export function useDecideStorefrontChanges(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: (body: { action: 'approve' | 'reject'; note: string | null; revision_id: string }) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}/changes`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
    onError: (error) => isStaleRefusal(error) && invalidate(),
  });
}

/** Publish the store's last approved version again, without pausing it
 *  (`POST …/revert`; `409 storefront_no_approved_version`). */
export function useRevertStorefront(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: (body: { note: string | null }) =>
      apiFetch<unknown>(`/admin/storefronts/${encodeURIComponent(id)}/revert`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export interface StorefrontPayoutInput {
  period_start: string;
  period_end: string;
  amount_cents: number;
  status: StorefrontPayoutStatus;
  paid_at: string | null;
  reference: string | null;
  note: string | null;
  /** "Pay more than owed": lets a paid payout exceed the balance (the
   *  backend notes it in the history). */
  allow_overpay?: boolean;
}

export function useRecordStorefrontPayout(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: ({ idempotencyKey, ...body }: StorefrontPayoutInput & { idempotencyKey: string }) =>
      apiFetch<StorefrontPayout>(`/admin/storefronts/${encodeURIComponent(id)}/payouts`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateStorefrontPayout(id: string) {
  const invalidate = useInvalidateStore(id);
  return useMutation({
    mutationFn: ({
      payoutId,
      ...body
    }: {
      payoutId: string;
      status: StorefrontPayoutStatus;
      paid_at?: string | null;
      reference?: string | null;
      allow_overpay?: boolean;
      note?: string | null;
      /** Why a payout is voided (owners read it; the void dialog requires it). */
      void_reason?: string | null;
    }) =>
      apiFetch<StorefrontPayout>(
        `/admin/storefronts/${encodeURIComponent(id)}/payouts/${encodeURIComponent(payoutId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: invalidate,
  });
}

export function useStorefrontInvites() {
  return useQuery({
    queryKey: storefrontAdminKeys.invites,
    queryFn: async () => {
      const body = await apiFetch<{ invites?: unknown }>('/admin/storefront-invites');
      return asArray<StorefrontInvite>(body.invites);
    },
    staleTime: 15_000,
  });
}

export function useCreateStorefrontInvites() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      idempotencyKey,
      ...body
    }: {
      count: number;
      note: string | null;
      expires_in_days: number;
      /** One per submit: a resend of the same submit carries the same key,
       *  so a backend that dedupes on it never mints a second batch. */
      idempotencyKey: string;
    }) => {
      const res = await apiFetch<{ invites?: unknown }>('/admin/storefront-invites', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      return asArray<{ code: string; expires_at: string | null }>(res.invites);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: storefrontAdminKeys.invites }),
  });
}

/** Revoke an unredeemed invite code (`DELETE /admin/storefront-invites/{id}`,
 *  idempotent; `409 invite_used` once redeemed). */
export function useRevokeStorefrontInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch<unknown>(`/admin/storefront-invites/${encodeURIComponent(inviteId)}`, {
        method: 'DELETE',
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: storefrontAdminKeys.invites }),
  });
}
